import {
  NOTES_LIST,
  NOTES_LIST_SUCCESS,
  NOTES_LIST_FAILURE,
  NOTE_GET,
  NOTE_GET_SUCCESS,
  NOTE_GET_FAILURE,
  NOTE_CREATE_SUCCESS,
  NOTE_UPDATE_SUCCESS,
  NOTE_UPDATE_CONFLICT,
  NOTE_DELETE_SUCCESS,
  NOTE_RESTORE_SUCCESS,
  NOTE_TAGS_LIST_SUCCESS,
  NOTE_TAG_CREATE_SUCCESS,
  NOTE_TAG_DELETE_SUCCESS,
  NOTE_SET_TAGS_SUCCESS,
  NOTE_SHARES_LIST,
  NOTE_SHARES_LIST_SUCCESS,
  NOTE_SHARES_LIST_FAILURE,
  NOTE_SHARE_SET_SUCCESS,
  NOTE_SHARE_REVOKE_SUCCESS,
  NOTE_LEAVE_SUCCESS,
  NOTE_SHARE_CANDIDATES,
  NOTE_SHARE_CANDIDATES_SUCCESS,
  NOTE_SHARE_CANDIDATES_FAILURE,
  NOTES_FILTER_SET,
  NOTE_CLEAR,
  NOTE_SAVE_STATE,
  LOGOUT,
} from '../constants';
import { NOTE_TABS } from '../../config/constants';

/**
 * Notes state.
 *
 * Its own slice rather than more keys on `MsgReducer`, which already carries chat, calls,
 * threads, notifications and saved messages and shares none of this state.
 *
 * Two shapes worth explaining:
 *
 *   • `list` holds the CURRENT tab's page only. Switching tabs replaces it rather than
 *     keeping a cache per tab — the counts and rows have to agree, and a stale cached tab
 *     showing a note that has since been revoked is worse than a brief loading state.
 *   • `note` is the open document. It carries `content`, which the list rows deliberately
 *     do not: a page of full rich-text documents is a multi-megabyte response.
 */

const initialState = {
  // the current page of the current tab
  list: [],
  count: 0,
  notes_loader: false,

  // the open note
  note: null,
  note_loader: false,
  note_not_found: false,
  /** 'idle' | 'saving' | 'saved' | 'error' — drives the editor's save indicator. */
  save_state: 'idle',
  /**
   * The server's copy, when a save was refused by the version guard. Non-null means the
   * conflict dialog is open. Cleared by resolving it either way.
   */
  conflict: null,

  // sidebar filter
  filter: { tab: NOTE_TABS.MINE, search: '', tags: [] },

  // my tag vocabulary
  tags: [],

  // the open note's access list
  shares: null,
  shares_loader: false,

  // colleagues the share dialog can offer
  candidates: [],
  candidates_loader: false,
};

/** Replace one note wherever it appears, without disturbing anything else. */
const patchList = (list, id, patch) =>
  list.map((row) => (row.id === id ? { ...row, ...patch } : row));

const NotesReducer = (state = initialState, action) => {
  switch (action.type) {
    // ─── list ───────────────────────────────────────────────────────────────
    case NOTES_LIST:
      return { ...state, notes_loader: true };

    case NOTES_LIST_SUCCESS: {
      const { data, offset } = action.payload ?? {};
      const rows = data?.rows ?? [];
      return {
        ...state,
        notes_loader: false,
        count: data?.count ?? 0,
        // `offset` decides append-vs-replace, which is what lets one action serve the
        // first load and infinite scroll. Deduped on id because a note edited between two
        // page reads shifts position and can arrive twice.
        list:
          offset > 0
            ? [...state.list, ...rows.filter((r) => !state.list.some((e) => e.id === r.id))]
            : rows,
      };
    }

    case NOTES_LIST_FAILURE:
      return { ...state, notes_loader: false };

    // ─── one note ───────────────────────────────────────────────────────────
    case NOTE_GET:
      return { ...state, note_loader: true, note_not_found: false };

    case NOTE_GET_SUCCESS:
      return {
        ...state,
        note_loader: false,
        note_not_found: false,
        note: action.payload,
        save_state: 'idle',
        conflict: null,
      };

    case NOTE_GET_FAILURE:
      return {
        ...state,
        note_loader: false,
        note: null,
        note_not_found: Boolean(action.payload?.notFound),
      };

    case NOTE_CREATE_SUCCESS: {
      const created = action.payload;
      if (!created) return state;
      // Prepended rather than refetched: the new note belongs at the top of "mine" by
      // `updatedAt` anyway, and a refetch would cost a round trip to learn what we already
      // know. `count` moves with it so the tab badge stays honest.
      return {
        ...state,
        note: created,
        list: state.filter.tab === NOTE_TABS.MINE ? [created, ...state.list] : state.list,
        count: state.filter.tab === NOTE_TABS.MINE ? state.count + 1 : state.count,
        save_state: 'idle',
      };
    }

    case NOTE_UPDATE_SUCCESS: {
      const updated = action.payload;
      if (!updated) return state;
      /**
       * `content` is taken from the response for the STORE copy but the editor never
       * re-reads it mid-session — see the note in `useNoteAutosave`. Replacing the
       * document under a typing cursor moves the caret and loses the selection, which is
       * why the editor holds its own copy and only takes `version` back.
       */
      return {
        ...state,
        note: state.note?.id === updated.id ? { ...state.note, ...updated } : state.note,
        list: patchList(state.list, updated.id, {
          title: updated.title,
          preview: updated.preview,
          is_pinned: updated.is_pinned,
          is_archived: updated.is_archived,
          color: updated.color,
          icon: updated.icon,
          version: updated.version,
          updatedAt: updated.updatedAt,
          tags: updated.tags ?? undefined,
        }),
        save_state: 'saved',
        conflict: null,
      };
    }

    case NOTE_UPDATE_CONFLICT:
      // Deliberately does NOT overwrite `note`. The user's unsaved work is in the editor,
      // and the dialog needs both copies to offer a choice between them.
      return { ...state, save_state: 'error', conflict: action.payload ?? null };

    case NOTE_DELETE_SUCCESS: {
      const id = action.payload?.id;
      return {
        ...state,
        list: state.list.filter((row) => row.id !== id),
        count: Math.max(0, state.count - 1),
        note: state.note?.id === id ? null : state.note,
      };
    }

    case NOTE_RESTORE_SUCCESS: {
      const restored = action.payload;
      if (!restored) return state;
      // Removed from whichever list is showing: a restore happens from the trash tab, and
      // the note has just stopped belonging there.
      return {
        ...state,
        list: state.list.filter((row) => row.id !== restored.id),
        count: Math.max(0, state.count - 1),
        note: state.note?.id === restored.id ? restored : state.note,
      };
    }

    // ─── tags ───────────────────────────────────────────────────────────────
    case NOTE_TAGS_LIST_SUCCESS:
      return { ...state, tags: action.payload?.rows ?? [] };

    case NOTE_TAG_CREATE_SUCCESS: {
      const tag = action.payload;
      if (!tag) return state;
      // Create is idempotent server-side, so this can be an existing tag arriving again.
      if (state.tags.some((t) => t.id === tag.id)) return state;
      return {
        ...state,
        tags: [...state.tags, tag].sort((a, b) => a.name.localeCompare(b.name)),
      };
    }

    case NOTE_TAG_DELETE_SUCCESS: {
      const tagId = action.payload?.tag_id;
      return {
        ...state,
        tags: state.tags.filter((t) => t.id !== tagId),
        // The tag is gone from every note too, and from the filter if it was selected —
        // leaving it there would filter by something that no longer exists and show an
        // empty list with no explanation.
        list: state.list.map((row) => ({
          ...row,
          tags: (row.tags ?? []).filter((t) => t.id !== tagId),
        })),
        filter: { ...state.filter, tags: state.filter.tags.filter((t) => t !== tagId) },
      };
    }

    case NOTE_SET_TAGS_SUCCESS: {
      const { note_id, tags } = action.payload ?? {};
      return {
        ...state,
        list: patchList(state.list, note_id, { tags }),
        note: state.note?.id === note_id ? { ...state.note, tags } : state.note,
      };
    }

    // ─── sharing ────────────────────────────────────────────────────────────
    case NOTE_SHARES_LIST:
      return { ...state, shares_loader: true };

    case NOTE_SHARES_LIST_SUCCESS:
      return { ...state, shares_loader: false, shares: action.payload };

    case NOTE_SHARES_LIST_FAILURE:
      return { ...state, shares_loader: false };

    case NOTE_SHARE_SET_SUCCESS: {
      // The response carries the grant row, not the enriched user — so the dialog refetches
      // rather than trying to splice a half-shaped entry in. Only the count on the list row
      // is patched here, because that is what the sidebar shows.
      const noteId = state.note?.id;
      if (!noteId) return state;
      const current = state.list.find((r) => r.id === noteId);
      const wasNew = action.payload?.created;
      return {
        ...state,
        list: wasNew
          ? patchList(state.list, noteId, { share_count: (current?.share_count ?? 0) + 1 })
          : state.list,
      };
    }

    case NOTE_SHARE_REVOKE_SUCCESS: {
      const noteId = state.note?.id;
      const userId = action.payload?.user_id;
      if (!noteId) return state;
      const current = state.list.find((r) => r.id === noteId);
      return {
        ...state,
        list: patchList(state.list, noteId, {
          share_count: Math.max(0, (current?.share_count ?? 1) - 1),
        }),
        shares: state.shares
          ? {
              ...state.shares,
              shares: state.shares.shares.filter((s) => s.user?.id !== userId),
              count: Math.max(0, state.shares.count - 1),
            }
          : state.shares,
      };
    }

    case NOTE_LEAVE_SUCCESS: {
      // Same list effect as a delete, different cause: the note is gone from MY view and
      // untouched for everybody else.
      const id = action.payload?.id;
      return {
        ...state,
        list: state.list.filter((row) => row.id !== id),
        count: Math.max(0, state.count - 1),
        note: state.note?.id === id ? null : state.note,
      };
    }

    case NOTE_SHARE_CANDIDATES:
      return { ...state, candidates_loader: true };

    case NOTE_SHARE_CANDIDATES_SUCCESS:
      return {
        ...state,
        candidates_loader: false,
        candidates: action.payload?.rows ?? [],
      };

    case NOTE_SHARE_CANDIDATES_FAILURE:
      return { ...state, candidates_loader: false };

    // ─── local ──────────────────────────────────────────────────────────────
    case NOTES_FILTER_SET: {
      const filter = { ...state.filter, ...action.payload };
      // A filter change invalidates the page. Cleared here rather than left for the fetch
      // to overwrite, so the list never shows the previous tab's rows under the new tab's
      // heading for a frame.
      return { ...state, filter, list: [], count: 0 };
    }

    case NOTE_CLEAR:
      // `note_not_found` is cleared here as well, and its absence was a bug: closing a note
      // that had 404'd left the flag set, so navigating back to `/notes` — where nothing had
      // been requested at all — rendered "This note no longer exists".
      return {
        ...state,
        note: null,
        note_not_found: false,
        shares: null,
        save_state: 'idle',
        conflict: null,
      };

    case NOTE_SAVE_STATE:
      return { ...state, save_state: action.payload };

    // Notes are private per user, so nothing here may survive a sign-out into the next
    // person's session on a shared machine.
    case LOGOUT:
      return initialState;

    default:
      return state;
  }
};

export default NotesReducer;
